import type React from "react";
import { useCallback, useEffect, useState, memo } from "react";
import { isAddress } from "viem";
import { useEnsAddress } from "wagmi";

export interface AddressInputProps {
  /** Callback when a valid address is confirmed */
  onAddressChange?: (address: string | null, ensName?: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Label text */
  label?: string;
  /** Custom id for the input */
  id?: string;
}

const AddressInputInner: React.FC<AddressInputProps> = ({
  onAddressChange,
  placeholder = "0x... or name.eth",
  label,
  id = "address-input",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [validationState, setValidationState] = useState<
    "neutral" | "valid" | "invalid" | "resolving"
  >("neutral");
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Debounce the input value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Check if it looks like an ENS name
  const looksLikeENS = useCallback((value: string) => {
    const ensPattern = /\.(eth|xyz|luxe|kred|art|club|id)$/i;
    return ensPattern.test(value);
  }, []);

  // ENS resolution hook - resolves on mainnet
  const shouldResolveENS =
    looksLikeENS(debouncedValue) && debouncedValue.length > 0;
  const {
    data: ensResolvedAddress,
    isLoading: isResolvingENS,
    error: ensError,
  } = useEnsAddress({
    name: shouldResolveENS ? debouncedValue : undefined,
    chainId: 1,
    query: {
      enabled: shouldResolveENS,
    },
  });

  // Validation logic
  useEffect(() => {
    if (debouncedValue.trim() === "") {
      setValidationState("neutral");
      setResolvedAddress(null);
      setErrorMessage("");
      onAddressChange?.(null);
      return;
    }

    if (isAddress(debouncedValue)) {
      setValidationState("valid");
      setResolvedAddress(debouncedValue);
      setErrorMessage("");
      onAddressChange?.(debouncedValue);
      return;
    }

    if (looksLikeENS(debouncedValue)) {
      if (isResolvingENS) {
        setValidationState("resolving");
        setErrorMessage("");
        return;
      }

      if (ensResolvedAddress) {
        setValidationState("valid");
        setResolvedAddress(ensResolvedAddress);
        setErrorMessage("");
        onAddressChange?.(ensResolvedAddress, debouncedValue);
        return;
      }

      setValidationState("invalid");
      if (ensError) {
        setErrorMessage(`ENS error: ${ensError.message}`);
      } else {
        setErrorMessage("ENS name not found");
      }
      setResolvedAddress(null);
      onAddressChange?.(null);
      return;
    }

    setValidationState("invalid");
    setErrorMessage("Invalid address format");
    setResolvedAddress(null);
    onAddressChange?.(null);
  }, [
    debouncedValue,
    ensResolvedAddress,
    isResolvingENS,
    ensError,
    onAddressChange,
    looksLikeENS,
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const getBorderColor = () => {
    switch (validationState) {
      case "valid":
        return "var(--success)";
      case "invalid":
        return "var(--error)";
      case "resolving":
        return "var(--ember-bright)";
      default:
        return "var(--charcoal-light)";
    }
  };

  return (
    <div className="form-group" style={{ marginBottom: label ? undefined : 0 }}>
      {label && <label htmlFor={id}>{label}</label>}
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        style={{
          width: "100%",
          borderColor: getBorderColor(),
          fontFamily: "monospace",
          fontSize: "0.875rem",
          transition: "border-color 0.2s",
        }}
      />
      {validationState === "resolving" && (
        <p
          style={{
            fontSize: "0.75rem",
            marginTop: "var(--space-xs)",
            color: "var(--ember-bright)",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
          }}
        >
          <span
            className="spinner"
            style={{ width: "12px", height: "12px", borderWidth: "1.5px" }}
          />
          Resolving ENS name...
        </p>
      )}
      {validationState === "valid" && resolvedAddress && (
        <p
          style={{
            fontSize: "0.75rem",
            marginTop: "var(--space-xs)",
            color: "var(--success)",
          }}
        >
          {looksLikeENS(inputValue) ? (
            <>
              Resolved to:{" "}
              <span style={{ fontFamily: "monospace" }}>
                {resolvedAddress.slice(0, 6)}...{resolvedAddress.slice(-4)}
              </span>
            </>
          ) : (
            "Valid address"
          )}
        </p>
      )}
      {validationState === "invalid" && errorMessage && (
        <p
          style={{
            fontSize: "0.75rem",
            marginTop: "var(--space-xs)",
            color: "var(--error)",
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

// Memoize to prevent re-renders from parent's polling hooks
// Compare only stable props (not the callback)
export const AddressInput = memo(AddressInputInner, (prev, next) => {
  return (
    prev.placeholder === next.placeholder &&
    prev.label === next.label &&
    prev.id === next.id
  );
});
